import type { TextKey } from './es';

/**
 * English dictionary. `satisfies` forces covering exactly the
 * same keys as es.ts: one key too few or too many is a compilation
 * error.
 */
export const en = {
  // ---- App / global ----
  'app.titulo': '{marca}',
  'app.tituloDocumento': '{marca}',

  // ---- Bottom navigation ----
  'nav.principal': 'Main navigation',
  'nav.inicio': 'Home',
  'nav.playas': 'Beaches',
  'nav.mapa': 'Map',
  'nav.informacion': 'Project information',
  'nav.sobre': 'About the app',
  'nav.acerca': 'About and terms',
  'nav.privacidad': 'Privacy and storage',
  'nav.abreFuera': '(opens outside the app)',
  'nav.enviarEmail': 'Send email',

  // ---- Language selector ----
  'selector.idioma': 'Language',

  // ---- Common ----
  'comun.verDetalleDe': 'View details for {nombre}',
  'comun.aKm': '{km} km away',
  'comun.noDisponible': 'Not available',
  'cielo.previsto': 'forecast',
  'cielo.noDisponible': 'Sky not available',

  // ---- Relative time ----
  'tiempo.ahoraMismo': 'updated just now',
  'tiempo.haceMin': 'updated {n} min ago',
  'tiempo.haceHoras': 'updated {n}h ago',
  'tiempo.haceDias': 'updated {n}d ago',

  // ---- Favorites ----
  'fav.marcar': 'Save {nombre} to favorites',
  'fav.quitar': 'Remove {nombre} from favorites',
  'fav.filtro': 'Show only favorites',
  'fav.vacio': 'No favorite beaches yet. Tap the star on a beach to save it here.',
  'home.favoritas': 'Your favorite beaches',
  'home.datosDeCache': 'Saved from your last visit',
  'home.actualizando': 'Refreshing the data…',

  // ---- Install as an app (PWA) ----
  'instalar.chip': 'Install app',
  'instalar.abrir': 'Open app',
  'instalar.iosTitulo': 'Add to Home Screen',
  'instalar.iosPaso1': 'Tap the Share button in the browser bar.',
  'instalar.iosPaso2': 'Choose "Add to Home Screen".',

  // ---- Home ----
  'home.subtitulo': 'Discover the best beaches in {region}',
  'home.mediaTemp': '{temp}° average',
  'home.playasBadge_one': '{count} beach',
  'home.playasBadge_other': '{count} beaches',
  'home.buscando': 'Finding the best beaches near you...',
  'home.locBloqueadaTitulo': 'Location blocked',
  'home.locBloqueadaSub': 'To enable it, go to your browser settings',
  'home.locNoDisponibleTitulo': 'Location not available',
  'home.locNoDisponibleSub': 'Tap to enable and see beaches near you',
  'home.cercaDeTi': 'Beaches closest to you',
  'home.mejorHoy': "Today's best beach",
  'home.mejorParaTi': 'Best for you today',
  'home.notaCercania': 'Prioritized by distance: some beaches score higher but are farther away',
  'home.mejorPuntuacion': 'Top score',
  'home.alternativas': 'Other good options',
  'home.verDetalles': 'View details',
  'home.verEnMapa': 'View on the map',
  'home.verEnMapaDe': 'View {nombre} on the map',
  'home.puntuacionAria': 'Score {n} out of 100',
  'home.sinDestacadas': 'No featured beaches today — check the full list',
  'home.sinDestacadasBoton': 'View beach list',
  'home.errorCondiciones': 'Could not load current conditions',
  'home.reintentar': 'Retry',
  'home.revisarAntes': 'Better check before you go',
  'home.banderaAria': '{bandera} flag',

  // ---- Beach list ----
  'lista.subtitulo': 'Check beach conditions',
  'lista.placeholder': 'Search beach or municipality...',
  'lista.buscarAria': 'Search beach or municipality',
  'lista.borrarBusqueda': 'Clear search',
  'lista.ordenarCercania': 'Sort by distance',
  'lista.ordenarAZ': 'Sort A-Z',
  'lista.cargando': 'Loading beaches...',
  'lista.contador_one': '{count} beach',
  'lista.contador_other': '{count} beaches',
  'lista.paraFiltro': 'for "{filtro}"',
  'lista.noEncontradas': 'No beaches found for "{filtro}"',
  'lista.vigiladaAria': 'Beach supervised by {operador}',
  'lista.webcamAria': 'Beach with webcam available',
  'lista.filtroWebcam': 'Show only beaches with webcam',
  'banderaAzul.distintivo': 'Blue Flag {year}',
  'banderaAzul.frase': 'This beach has been awarded the Blue Flag {year}.',
  'banderaAzul.masInfo': 'More information at',
  'lista.banderaAzulAria': 'Beach with Blue Flag',
  'lista.datosLocales': 'Offline: showing saved data, it may be out of date',
  'lista.datosNoDisponibles': 'Could not load beaches or saved data',

  // ---- Map ----
  'mapa.subtitulo': 'Explore the beaches on the map',
  'mapa.municipio': 'Municipality:',
  'mapa.vigilada': 'Supervised by {operador}',
  'mapa.sinInfoCruzRoja': 'No lifeguard info',
  'mapa.sinVigilancia': 'No lifeguard service',
  'mapa.webcamDisponible': 'Webcam available',
  'mapa.verDetalles': 'View details',
  'mapa.tuUbicacion': 'Your current location',
  'mapa.localizarme': 'Locate me',
  'mapa.banderaRoja': 'Red flag',
  'mapa.vientoFuerteKmh': 'Strong wind ({kmh} km/h)',
  'mapa.leyendaBuenas': 'Good conditions',
  'mapa.leyendaRegular': 'Use caution',
  'mapa.leyendaMalas': 'Not advised',
  'mapa.leyendaBandera': 'Flag hoisted',

  // ---- Wind levels ----
  'viento.sinViento': 'no wind',
  'viento.brisaSuave': 'gentle breeze',
  'viento.moderado': 'moderate wind',
  'viento.fuerte': 'strong wind',

  // ---- Flag (Cruz Roja) ----
  'bandera.negra': 'Black Flag',
  'bandera.roja': 'Red Flag',
  'bandera.amarilla': 'Yellow Flag',
  'bandera.verde': 'Green Flag',
  'bandera.sinDatos': 'No data',
  'bandera.fueraDeHorario': 'Outside hours',
  'bandera.ultimaRegistrada': 'Last recorded flag: {bandera}',

  // ---- Relative dates ----
  'fecha.hoy': 'Today',
  'fecha.manana': 'Tomorrow',
  'fecha.pasadoManana': 'Day after tomorrow',

  // ---- Beach detail ----
  'detalle.titulo': 'Detail',
  'detalle.volver': 'Back',
  'detalle.puntuacion': "Today's score",
  'detalle.comoSeCalcula': 'How it works',
  'detalle.scoreInfo.intro': 'The score (0–100) sums up how appealing and safe the beach is today. It combines the current conditions, giving more weight to what matters most for a good beach day:',
  'detalle.scoreInfo.sol': 'Sun and sky: the clearer, the better.',
  'detalle.scoreInfo.temp': 'Temperature: goes up in pleasant weather and down when cold.',
  'detalle.scoreInfo.bandera': 'Lifeguard flag: green adds, yellow subtracts, and red or black sink the score.',
  'detalle.scoreInfo.viento': 'Wind: the lighter, the better.',
  'detalle.scoreInfo.oleaje': 'Sea state: calm water scores higher (surf beaches are not penalised).',
  'detalle.scoreInfo.datos': 'Available data: goes up when today has both weather and flag.',
  'detalle.scoreInfo.lluvia': 'Rain: if it is raining or expected, the score is capped so it never shows as a "good day".',
  'detalle.scoreInfo.peligro': 'Danger: with a black flag, storm or warning, the beach is flagged to avoid it.',
  'detalle.scoreInfo.cierre': 'It is a quick guide, not an exact measurement.',
  'detalle.scoreInfo.deEstaPlaya': 'Today, at this beach:',
  'detalle.scoreInfo.puntos': '{n}/{max}',
  'detalle.scoreInfo.datosCompletos': 'weather and flag',
  'detalle.scoreInfo.datosParciales': 'some data missing',
  'detalle.scoreInfo.sinDato': 'no data',
  'detalle.scoreInfo.sinBanderaAhora': 'no flag right now',
  'detalle.scoreInfo.topeLluvia': 'It is raining: the score is capped at {n} however well the rest scores.',
  'detalle.scoreInfo.topeLluviaPrevista': 'Rain is expected: the score is capped at {n} however well the rest scores.',
  'detalle.pronostico.titulo': 'Next 4 h',
  'detalle.pronostico.mejora': 'Improving',
  'detalle.pronostico.empeora': 'Getting worse',
  'detalle.pronostico.estable': 'No change',
  'detalle.pronostico.puntos': '{n} points',
  'detalle.pronostico.sinDatos': 'No hourly forecast right now.',
  'detalle.pronostico.ariaHora': 'At {hora}: {nubes}% cloud, {temp} degrees, wind {viento} metres per second',
  'detalle.pronostico.ariaHoraLluvia': 'At {hora}: rain expected, {temp} degrees, wind {viento} metres per second',
  'detalle.pronostico.causa.despeja': 'clearing up',
  'detalle.pronostico.causa.nubla': 'clouding over',
  'detalle.pronostico.causa.subeTemperatura': 'warming up',
  'detalle.pronostico.causa.bajaTemperatura': 'cooling down',
  'detalle.pronostico.causa.amainaViento': 'wind dropping',
  'detalle.pronostico.causa.arreciaViento': 'wind picking up',
  'detalle.pronostico.causa.lluviaPrevista': 'rain expected',
  'detalle.pronostico.aria': 'Next 4 hours: {direccion}, {causa}',
  'detalle.pronostico.ariaSinCausa': 'Next 4 hours: {direccion}',
  'ventana.mejor': 'Best time to go: {inicio}–{fin}',
  'ventana.cambio.despeja': 'From {hora} it clears up',
  'ventana.cambio.nubla': 'From {hora} it clouds over',
  'ventana.cambio.subeTemperatura': 'From {hora} it warms up',
  'ventana.cambio.bajaTemperatura': 'From {hora} it cools down',
  'ventana.cambio.amainaViento': 'From {hora} the wind eases',
  'ventana.cambio.arreciaViento': 'From {hora} the wind picks up',
  'ventana.cambio.lluviaPrevista': 'From {hora} rain is expected',
  'ventana.hastaFin': 'Good time until {fin}',
  'ventana.motivo.sinLluvia': 'Chosen as the stretch with no rain expected',
  'ventana.motivo.despeja': 'Chosen as the clearest hours',
  'ventana.motivo.subeTemperatura': 'Chosen as the warmest hours',
  'ventana.motivo.amainaViento': 'Chosen as the hours with the least wind',
  'ventana.sinCambios': 'Nothing turning worse before the day closes',
  'detalle.pronostico.tituloRestoDia': 'Rest of today ({dia})',
  'detalle.pronostico.horasAnteriores': 'Show earlier hours',
  'detalle.pronostico.horasSiguientes': 'Show more hours',
  'home.frase.muyBien': '{nombre} is looking great today',
  'home.frase.bien': '{nombre} is looking good today',
  'home.frase.regular': '{nombre} is just okay today',
  'detalle.cargando': 'Loading beach data...',
  'detalle.errorCarga': 'Could not load beach details',
  'detalle.sinRespuesta': 'No response from the server',
  'detalle.comoLlegar': 'Directions',
  'detalle.verEnMapa': 'View on the map',
  'detalle.estadoBano': 'Swimming conditions (per {operador})',
  'detalle.banderaAria': 'flag',
  'detalle.vigilancia': 'Lifeguard hours: {horario}',
  'detalle.registradaHoy': 'Recorded today at {hora}',
  'detalle.registradaAyer': 'Recorded yesterday at {hora}',
  'detalle.registradaFecha': 'Recorded on {fecha} at {hora}',
  'detalle.temperatura': 'Temperature',
  'detalle.agua': 'Water',
  'detalle.viento': 'Wind',
  'detalle.oleaje': 'Waves',
  'detalle.bandera': 'Flag',
  'detalle.cielo': 'Sky',
  'detalle.sensacion': 'Feels like',
  'detalle.indiceUV': 'Max UV index',
  'detalle.max': 'Max.',
  'detalle.aguaGrados': 'Water {temp}°C',
  'detalle.lloviendoAhora': 'Raining now',
  'detalle.lluviaUltimaHora': 'Rain in the last hour',
  'detalle.lluviaPrevistaHora': 'Rain expected around {hora}',
  'detalle.lluviaPrevistaHoy': 'Rain expected today',
  'detalle.previsionAemet': 'Forecast (AEMET)',
  'detalle.periodoManana': 'Morning',
  'detalle.periodoTarde': 'Afternoon',
  'detalle.sensacionTermica': 'Feels like',
  'detalle.avisoLitoral': 'Coastal warning',
  'detalle.mareas': 'Tides',
  'detalle.expandir': 'Expand',
  'detalle.contraer': 'Collapse',
  'detalle.infoPlaya': 'Beach information',
  'detalle.dimensiones': 'Dimensions',
  'detalle.tipo': 'Type',
  'detalle.arena': 'Sand',
  'detalle.acceso': 'Access',
  'detalle.parking': 'Parking',
  'detalle.bus': 'Bus',
  'detalle.hospital': 'Hospital',
  'detalle.servicios': 'Services and features',
  'detalle.zonaAvisos': 'Warning zone: {zona}',
  'detalle.datosMeteo': 'Weather data: {fuente}',

  // ---- SEO (document head per route) ----
  'seo.tituloInicio': '{region} beaches: the best beach for today',
  'seo.descInicio': 'Compare {region} beaches right now: score, flag, weather and forecast to pick a beach today.',
  'seo.tituloLista': 'All the beaches of {region} | {marca}',
  'seo.descLista': 'Full list of {region} beaches, with search, distance, flags and services.',
  'seo.tituloAcerca': 'About and terms | {marca}',
  'seo.descAcerca':
    'What {marca} is, its independence, limits of liability, data sources and contact.',
  'seo.tituloPrivacidad': 'Privacy and storage | {marca}',
  'seo.descPrivacidad':
    'What {marca} processes: optional geolocation, local storage, providers and your rights.',
  'seo.tituloMapa': '{region} beach map | {marca}',
  'seo.descMapa': 'Interactive map with every beach in {region} and its status.',
  'seo.tituloDetalle': '{nombre} beach ({municipio}): flag, weather and tides today | {marca}',
  'seo.descDetalle': 'Status of {nombre} beach, in {municipio}: current flag, weather, forecast and today\'s tides.',
  'seo.tituloMunicipio': 'Beaches in {municipio}: current conditions | {marca}',
  'seo.descMunicipio': 'The beaches of {municipio}, in {region}, each linking to today\'s flag, weather and tides.',
  'seo.tituloMunicipios': 'Municipalities with beaches in {region} | {marca}',
  'seo.descMunicipios': 'Every municipality in {region} with a beach in the catalog, with access to each one\'s beaches.',
  'municipios.titulo': 'Municipalities with beaches',
  'municipios.intro': 'The municipalities of {region} with beaches in the catalog. Each one leads to its beaches and their status.',
  'municipio.verPlayas': 'See all the beaches of {municipio}',
  'detalle.municipio': 'Municipality',
  'noEncontrada.texto': 'This address does not exist in the app.',
  'detalle.compartir': 'Share',
  'detalle.generandoImagen': 'Building image…',
  'detalle.otrasPlayasMunicipio': 'Other beaches in the municipality of {municipio}',
  'detalle.enlaceCopiado': 'Link copied',
  'seo.tituloNoEncontrada': 'Page not found | {marca}',
  'seo.descNoEncontrada': 'This address does not match any beach or municipality in the {region} catalog.',

  // ---- Municipality and curated landing pages ----
  'municipio.titulo': 'Beaches of the municipality of {municipio}',
  'municipio.intro': 'The beaches of {municipio}, each linking to today\'s conditions.',
  'municipio.desconocido': 'We do not know that municipality. See the full beach list.',
  'landing.playas-con-webcam.titulo': 'Beaches with a webcam in {region}',
  'landing.playas-con-webcam.intro': 'Beaches in {region} with a public webcam to check the area before going. The link opens each camera\'s external service; the app does not check whether it is live.',
  'landing.playas-accesibles.titulo': 'Accessible beaches in {region}',
  'landing.playas-accesibles.intro': 'Beaches marked accessible in the {region} catalog. Fixed beach information: services may be seasonal.',
  'landing.playas-con-socorrista.titulo': 'Lifeguarded beaches in {region}',
  'landing.playas-con-socorrista.intro': 'Beaches in {region} with a registered lifeguard post. Today\'s flag and hours load on each beach\'s page.',
  'landing.playas-para-surf.titulo': 'Surf beaches in {region}',
  'landing.playas-para-surf.intro': 'Beaches flagged for surf in the {region} catalog. Today\'s sea conditions are on each beach\'s page.',
  'landing.playas-con-perros.titulo': 'Dog-friendly beaches in {region}',
  'landing.playas-con-perros.intro': 'Beaches in the {region} catalog marked as pet-friendly. Rules and hours can change by season: check with the town council before going.',
  'landing.playas-nudistas.titulo': 'Nudist beaches in {region}',
  'landing.playas-nudistas.intro': 'Beaches in the {region} catalog marked as nudist.',

  // ---- Attributions required by each source ----
  // AEMET's notice keeps its official Spanish wording: it is the phrase the
  // agency requires, not a label of ours to translate.
  'atribucion.aemet':
    'Información elaborada utilizando, entre otras, la obtenida de la Agencia Estatal de Meteorología. Source: {fuente}.',
  'atribucion.openweather': 'Weather data provided by {fuente}.',
  'atribucion.openmeteo':
    'Weather data by {fuente}, adapted by {marca}: it is transformed to compute the score.',
  'atribucion.banderas': 'Flag status published by {fuente}.',
  'atribucion.independiente':
    '{marca} is an independent project: none of these sources endorses it or collaborates with it.',

  // ---- ⓘ control labels ----
  'info.aviso': 'Notice',
  'info.fuente': 'Source',
  'info.sobreDatos': 'About this data',
  'info.aria.bandera': 'Notice about the flag',
  'info.aria.ranking': 'Notice about the recommendation',
  'info.aria.prevision': 'Source of the forecast',
  'info.aria.horas': 'Source of the next hours',
  'info.aria.vigilancia': 'Source of the lifeguard service',
  'info.aria.ficha': 'About the data on this page',

  // ---- Notices next to sensitive information ----
  'aviso.banderas':
    'Indicative information that may be out of date. Always check the flag flying at the beach and follow the instructions of the lifeguards and the authorities.',
  'aviso.ranking':
    'Automatic recommendation based on forecasts and available data. It does not guarantee the safety or the actual conditions of the beach.',

  // ---- Data provenance ----
  'datos.fuente': 'Data from {fuente}',
  'datos.noDisponible': 'Data not available: the latest observation is too old,',
  'datos.estimados': 'Values estimated from other data: {campos}.',
  'datos.estimado.sensacion': 'feels like',
  'datos.estimado.viento': 'wind',
  'datos.estimado.oleaje': 'waves',
  'datos.estimado.uv': 'UV index',
  'datos.estimado.agua': 'water temperature',
  'datos.calculado': 'Data computed on',
  'datos.desdeCache': 'served from cache',
  'datos.enDirectoFuente': 'Live observation from {fuente}',
  'datos.webcamExterna': 'External service: the app does not check if it is live',

  // ---- Tides ----
  'marea.subiendo': 'Rising',
  'marea.bajando': 'Falling',
  'marea.pleamar': 'High tide',
  'marea.bajamar': 'Low tide',
  'marea.referenciaAviso': 'This beach has no tide table of its own. Showing {playa}, {km} km away.',

  // ---- Cruz Roja (card) ----
  'cruzroja.vigilanciaCobertura': 'Lifeguard service and coverage',
  'cruzroja.sinInfo': '{operador} information not available yet',
  'cruzroja.banderaActual': 'Current flag',
  'cruzroja.coberturaDesde': 'Coverage from',
  'cruzroja.coberturaHasta': 'Coverage until',
  'cruzroja.horario': 'Hours',
  'cruzroja.ultimaActualizacion': 'Last updated',

  // ---- Webcam (detail card) ----
  'webcam.enDirecto': 'Live webcam',
  'webcam.vistaPanoramica': 'Panoramic view of the area',
  'webcam.cercana': 'Nearby webcam',
  'webcam.abrir': 'Open webcam',

  // ---- Beach attributes ----
  'attr.duchas': 'Showers',
  'attr.aseos': 'Toilets',
  'attr.parking': 'Parking',
  'attr.accesible': 'Accessible',
  'attr.chiringuito': 'Beach bar',
  'attr.surf': 'Surf',
  'attr.mascotas': 'Pets allowed',
  'attr.socorrismo': 'Lifeguards',
  'attr.nudista': 'Nudist',
  'attr.accesoBanista': 'Swimmer access',
  'attr.submarinismo': 'Scuba diving',
} satisfies Record<TextKey, string>;
